import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { BACKEND_URL } from "../lib/api";
import { useT } from "../i18n/LanguageContext";

const STORAGE_KEY = "wz_chat";

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { session_id: null, messages: [] };
  } catch {
    return { session_id: null, messages: [] };
  }
}

function saveState(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
}

const GREETING = "Hi! I'm the WattZap Energy Savings Assistant. Ask me about solar, EMS Lite, EnergyScan, tariffs or power factor — I'll help you find savings.";

export default function ChatWidget() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [state, setState] = useState(loadState);
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { saveState(state); }, [state]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [state.messages, open]);
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const visibleMessages = state.messages.length === 0
    ? [{ role: "assistant", content: GREETING }]
    : state.messages;

  async function send() {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    const newUserMsg = { role: "user", content: text };
    setState((s) => ({ ...s, messages: [...s.messages, newUserMsg, { role: "assistant", content: "" }] }));
    setStreaming(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: state.session_id, message: text }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      let sid = state.session_id;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop() || "";
        for (const frame of frames) {
          if (!frame.trim()) continue;
          const lines = frame.split("\n");
          const evLine = lines.find((l) => l.startsWith("event:"));
          const dataLine = lines.find((l) => l.startsWith("data:"));
          if (!dataLine) continue;
          const raw = dataLine.slice(5).startsWith(" ") ? dataLine.slice(6) : dataLine.slice(5);
          if (evLine && evLine.includes("meta")) {
            try { const meta = JSON.parse(raw); if (meta.session_id) sid = meta.session_id; } catch {}
            continue;
          }
          if (evLine && evLine.includes("done")) continue;
          acc += raw.replace(/\\n/g, "\n");
          setState((s) => {
            const msgs = [...s.messages];
            msgs[msgs.length - 1] = { role: "assistant", content: acc };
            return { ...s, session_id: sid, messages: msgs };
          });
        }
      }
      setState((s) => ({ ...s, session_id: sid }));
    } catch (err) {
      setState((s) => {
        const msgs = [...s.messages];
        msgs[msgs.length - 1] = { role: "assistant", content: "Sorry — our assistant is unreachable right now. Please WhatsApp us or book a free EnergyScan." };
        return { ...s, messages: msgs };
      });
    } finally {
      setStreaming(false);
    }
  }

  function reset() {
    setState({ session_id: null, messages: [] });
  }

  useEffect(() => {
    const fn = () => setOpen(true);
    window.addEventListener("wattzap:open-chat", fn);
    return () => window.removeEventListener("wattzap:open-chat", fn);
  }, []);

  return (
    <>
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-5 right-5 z-50 w-[min(94vw,380px)] max-h-[80vh] rounded-2xl bg-white border border-slate-200 shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="bg-[#071A2D] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-wz-gradient flex items-center justify-center text-[#071A2D]">
                <Sparkles size={16} />
              </div>
              <div>
                <div className="text-sm font-heading">WattZap Assistant</div>
                <div className="text-[10px] text-white/60">Powered by AI · energy questions only</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="chat-widget-reset" onClick={reset} title="New chat" className="text-white/60 hover:text-white text-[10px] uppercase tracking-wider px-2 py-1">Reset</button>
              <button data-testid="chat-widget-close" onClick={() => setOpen(false)} aria-label="Close" className="text-white/70 hover:text-white p-1">
                <X size={18} />
              </button>
            </div>
          </div>

          <div ref={scrollRef} data-testid="chat-widget-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-mist">
            {visibleMessages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] text-sm leading-relaxed rounded-2xl px-3.5 py-2.5 ${
                  m.role === "user"
                    ? "bg-[#071A2D] text-white rounded-br-sm"
                    : "bg-white border border-slate-100 text-graphite rounded-bl-sm"
                }`}>
                  {(m.content ? t(m.content) : (streaming && i === visibleMessages.length - 1 ? "…" : ""))}
                </div>
              </div>
            ))}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-slate-100 bg-white p-2 flex items-center gap-2"
          >
            <input
              ref={inputRef}
              data-testid="chat-widget-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about your bill, solar, PF…"
              disabled={streaming}
              className="flex-1 rounded-full border border-slate-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF] disabled:opacity-60"
            />
            <button
              type="submit"
              data-testid="chat-widget-send"
              disabled={streaming || !input.trim()}
              className="btn-gradient w-9 h-9 rounded-full inline-flex items-center justify-center disabled:opacity-50"
              aria-label="Send"
            >
              <Send size={15} />
            </button>
          </form>
          <div className="bg-white px-3 pb-2 text-center text-[10px] text-slate-400" data-testid="chat-ai-disclaimer">
            {t("AI-generated responses — informational only, not professional advice.")}
          </div>
        </div>
      )}
    </>
  );
}
